import { Head, Link, useForm, router } from '@inertiajs/react'
import { useState } from 'react'
import SuperAdminLayout from '../_SuperAdminLayout'

interface PendingTenant {
  id: number
  proposed_org_name: string
  proposed_slug: string | null
  contact_email: string
  contact_first_name: string
  contact_last_name: string
  contact_full_name: string
  status: string
  notes: string | null
  rejection_reason: string | null
  requested_at_display: string
  reviewed_at_display: string | null
  provisioned_organization_id: number | null
}

interface Props { pending_tenant: PendingTenant; errors?: Record<string, string | string[]> }

export default function PendingTenantShow({ pending_tenant: pt, errors = {} }: Props) {
  const [showReject, setShowReject] = useState(false)
  const [rejectReason, setRejectReason] = useState('')

  const { data, setData, put, processing } = useForm({
    pending_tenant: {
      proposed_org_name: pt.proposed_org_name,
      proposed_slug: pt.proposed_slug || '',
      contact_email: pt.contact_email,
      contact_first_name: pt.contact_first_name,
      contact_last_name: pt.contact_last_name,
      notes: pt.notes || ''
    }
  })

  const save = (e: React.FormEvent) => {
    e.preventDefault()
    put(`/super_admin/pending_tenants/${pt.id}`)
  }

  const approve = () => router.post(`/super_admin/pending_tenants/${pt.id}/approve`)
  const reject = () => router.post(`/super_admin/pending_tenants/${pt.id}/reject`, { rejection_reason: rejectReason })

  const isPending = pt.status === 'pending'

  return (
    <SuperAdminLayout>
      <Head title={`Pending: ${pt.proposed_org_name}`} />
      <Link href="/super_admin/pending_tenants" className="text-sm text-[#ED1C24] hover:underline">&larr; Pending tenants</Link>
      <div className="flex items-start justify-between mt-2 mb-6">
        <h1 className="text-2xl font-bold text-text-primary">{pt.proposed_org_name}</h1>
        <span className={`text-xs font-semibold px-2 py-0.5 rounded-full ${pt.status === 'approved' ? 'bg-[#15532E]/10 text-[#15532E]' : pt.status === 'rejected' ? 'bg-[#ED1C24]/10 text-[#ED1C24]' : 'bg-[#F0AD4E]/10 text-[#8a5a00]'}`}>{pt.status}</span>
      </div>

      <div className="bg-white border border-stone/20 rounded-lg p-6 max-w-3xl space-y-4">
        <div className="text-xs text-text-muted">Requested {pt.requested_at_display}{pt.reviewed_at_display ? ` · Reviewed ${pt.reviewed_at_display}` : ''}</div>

        <form onSubmit={save} className="space-y-3">
          <Field label="Organization name" value={data.pending_tenant.proposed_org_name} onChange={v => setData('pending_tenant', { ...data.pending_tenant, proposed_org_name: v })} disabled={!isPending} />
          <Field label="Proposed slug" value={data.pending_tenant.proposed_slug} onChange={v => setData('pending_tenant', { ...data.pending_tenant, proposed_slug: v })} disabled={!isPending} hint="Auto-generated from name when blank" />
          <div className="grid grid-cols-2 gap-3">
            <Field label="Admin first name" value={data.pending_tenant.contact_first_name} onChange={v => setData('pending_tenant', { ...data.pending_tenant, contact_first_name: v })} disabled={!isPending} />
            <Field label="Admin last name" value={data.pending_tenant.contact_last_name} onChange={v => setData('pending_tenant', { ...data.pending_tenant, contact_last_name: v })} disabled={!isPending} />
          </div>
          <Field label="Admin email" type="email" value={data.pending_tenant.contact_email} onChange={v => setData('pending_tenant', { ...data.pending_tenant, contact_email: v })} disabled={!isPending} />
          <div>
            <label className="block text-sm font-medium text-text-primary mb-1">Notes</label>
            <textarea
              rows={3}
              value={data.pending_tenant.notes}
              onChange={e => setData('pending_tenant', { ...data.pending_tenant, notes: e.target.value })}
              disabled={!isPending}
              className="w-full px-3 py-2 border border-stone/30 rounded-lg disabled:bg-[#F5F5F5]"
            />
          </div>

          {pt.rejection_reason && (
            <div className="text-sm text-[#ED1C24] bg-[#ED1C24]/5 px-3 py-2 rounded">Rejection reason: {pt.rejection_reason}</div>
          )}

          {pt.provisioned_organization_id && (
            <div className="text-sm text-[#15532E] bg-[#15532E]/5 px-3 py-2 rounded">
              Provisioned tenant: <Link className="underline" href={`/super_admin/tenants/${pt.provisioned_organization_id}`}>view tenant</Link>
            </div>
          )}

          {isPending && (
            <div className="flex justify-end gap-2 pt-2 border-t border-stone/10">
              <button disabled={processing} type="submit" className="px-3 py-2 border border-stone/30 rounded-lg text-sm">Save edits</button>
              <button type="button" onClick={() => setShowReject(true)} className="px-3 py-2 border border-[#ED1C24] text-[#ED1C24] rounded-lg text-sm font-semibold">Reject</button>
              <button type="button" onClick={approve} className="px-3 py-2 bg-[#ED1C24] text-white rounded-lg text-sm font-semibold hover:bg-[#C7161E]">Approve & invite admin</button>
            </div>
          )}
        </form>
      </div>

      {showReject && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 max-w-md w-full">
            <h2 className="text-lg font-bold text-text-primary">Reject pending tenant?</h2>
            <p className="text-sm text-text-muted mt-2">The pending record will be marked rejected. Optionally include a reason for the audit log.</p>
            <textarea rows={3} value={rejectReason} onChange={e => setRejectReason(e.target.value)} className="mt-3 w-full px-3 py-2 border border-stone/30 rounded-lg text-sm" placeholder="Optional reason" />
            <div className="flex justify-end gap-2 mt-4">
              <button onClick={() => setShowReject(false)} className="px-3 py-2 border border-stone/30 rounded-lg text-sm">Cancel</button>
              <button onClick={reject} className="px-3 py-2 bg-[#ED1C24] text-white rounded-lg text-sm font-semibold">Reject</button>
            </div>
          </div>
        </div>
      )}
    </SuperAdminLayout>
  )
}

function Field({ label, value, onChange, type = 'text', disabled, hint }: { label: string; value: string; onChange: (v: string) => void; type?: string; disabled?: boolean; hint?: string }) {
  return (
    <div>
      <label className="block text-sm font-medium text-text-primary mb-1">{label}</label>
      <input type={type} disabled={disabled} value={value} onChange={e => onChange(e.target.value)} className="w-full px-3 py-2 border border-stone/30 rounded-lg disabled:bg-[#F5F5F5]" />
      {hint && <p className="text-xs text-text-muted mt-1">{hint}</p>}
    </div>
  )
}
